// Drops kafkajs 2.2.4's cosmetic TimeoutNegativeWarning; scoped so our own
// negative-timeout bugs still surface. Rationale + design: ISSUES.md § KAFKAJS-TIMEOUT-WARNING.

type EmitWarning = typeof process.emitWarning;

let originalEmitWarning: EmitWarning | null = null;
let patchedEmitWarning: EmitWarning | null = null;

export function isKafkaTimeoutNegativeWarning(name: string | undefined, stack: string | undefined): boolean {
  return name === 'TimeoutNegativeWarning' && (stack ?? '').includes('kafkajs');
}

// Process-global singleton (no ref-count): the reporter owns exactly one
// KafkaEventProducer, so install/teardown track that single lifecycle.
export function suppressKafkaTimeoutWarning(): void {
  if (patchedEmitWarning) return;
  const original = process.emitWarning;
  const callOriginal = original.bind(process) as (w: string | Error, ...a: unknown[]) => void;
  const patched = ((warning: string | Error, ...rest: unknown[]): void => {
    const type = typeof rest[0] === 'string' ? rest[0] : (rest[0] as { type?: string } | null | undefined)?.type;
    const name = warning instanceof Error ? warning.name : type;
    // Name check first so the stack is only materialized for the candidate warning.
    if (name === 'TimeoutNegativeWarning' && isKafkaTimeoutNegativeWarning(name, new Error().stack ?? undefined)) {
      return;
    }
    callOriginal(warning, ...rest);
  }) as EmitWarning;
  originalEmitWarning = original;
  patchedEmitWarning = patched;
  process.emitWarning = patched;
}

export function restoreKafkaTimeoutWarning(): void {
  // Only tear down when OUR patch is still the current emitWarning. If something
  // layered another patch on top, leave our refs intact so we neither orphan the
  // buried patch nor let a later suppress() double-wrap.
  if (process.emitWarning !== patchedEmitWarning) return;
  if (originalEmitWarning) process.emitWarning = originalEmitWarning;
  originalEmitWarning = null;
  patchedEmitWarning = null;
}
